/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { observable, action, decorate, runInAction, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import IdleTimer from 'react-idle-timer';

import { autoLogoutTimeoutInMinutes } from '../helpers/settings';

// expected props
// - authentication
// - app
class AutoLogout extends React.Component {
  constructor(props) {
    super(props);

    runInAction(() => {
      this.dialogCountDown = undefined;
      this.intervalId = undefined;
    });
  }

  get app() {
    return this.props.app;
  }

  get authentication() {
    return this.props.authentication;
  }

  get modalOpen() {
    return this.dialogCountDown >= 0;
  }

  componentDidMount() {}

  clearInterval() {
    if (!_.isUndefined(this.intervalId)) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    this.dialogCountDown = undefined;
  }

  startDialogCountDown = () => {
    if (!_.isUndefined(this.intervalId)) return;
    this.dialogCountDown = 60;

    this.intervalId = setInterval(async () => {
      // eslint-disable-next-line consistent-return
      runInAction(() => {
        if (this.dialogCountDown <= 0) {
          return this.doLogout();
        }
        this.dialogCountDown -= 1;
      });
    }, 1000);
  };

  cancelDialogCountDown = () => {
    this.clearInterval();
  };

  doLogout = async () => {
    this.clearInterval();
    return this.authentication.logout({ autoLogout: true });
  };

  handleLogout = async event => {
    event.preventDefault();
    event.stopPropagation();
    return this.doLogout();
  };

  render() {
    const authenticated = this.app.userAuthenticated;
    if (!authenticated) return null;
    return (
      <>
        <IdleTimer timeout={1000 * 60 * autoLogoutTimeoutInMinutes} onIdle={this.startDialogCountDown} />
        {this.modalOpen && (
          <div className="rmit-modal-overlay">
            <div className="rmit-modal">
              <div className="rmit-modal-icon rmit-modal-icon-warning">
                <i className="clock outline icon" />
              </div>
              <h2 className="rmit-modal-title">Are you still there?</h2>
              <p className="rmit-modal-text">For security purposes, you will be logged out in</p>
              <div className="rmit-modal-countdown">{this.dialogCountDown}</div>
              <p className="rmit-modal-text">seconds</p>
              <div className="rmit-modal-actions">
                <button className="rmit-modal-btn rmit-modal-btn-secondary" onClick={this.handleLogout}>
                  Log Out
                </button>
                <button className="rmit-modal-btn rmit-modal-btn-primary" onClick={this.cancelDialogCountDown}>
                  Keep Me Logged In
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(AutoLogout, {
  app: computed,
  authentication: computed,
  modalOpen: computed,
  intervalId: observable,
  dialogCountDown: observable,
  startDialogCountDown: action,
  doLogout: action,
  handleLogout: action,
  cancelDialogCountDown: action,
  clearInterval: action,
});

export default inject('authentication', 'app')(observer(AutoLogout));
